import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { FormikProps, useFormik } from 'formik';

import { useSyncInspection } from '../services/Amenities/useAmenitiesService';
import { TFetchInspectionProps } from '../services/Inspection/types';
import {
  useFetchInspection,
  useFetchInspectionQuantity,
} from '../services/Inspection/useInspectionService';

const DEFAULT_PAGE_SIZE = '10';

type Quantity = ReturnType<typeof useFetchInspectionQuantity>;
type Inspections = ReturnType<typeof useFetchInspection>;

export interface UseInspectionsHook {
  formik: FormikProps<{ searchTerm: string; searchField: string }>;
  searchTerm: string;
  searchField: string;
  quantityData: Quantity['data'];
  quantityLoading: Quantity['loading'];
  quantityError: Quantity['error'];
  fetchInspectionQuantity: Quantity['fetchInspectionQuantity'];

  inspectionsData: Inspections['data'];
  inspectionsLoading: Inspections['loading'];
  inspectionsError: Inspections['error'];

  currentPage: number;
  pageSize: number;
  setPageSize: (n: number) => void;
  setCurrentPage: (n: number) => void;
  handleGetInspections: (today?: boolean) => void;
}

const useInspections = (): UseInspectionsHook => {
  const {
    data: quantityData,
    loading: quantityLoading,
    error: quantityError,
    fetchInspectionQuantity,
  } = useFetchInspectionQuantity();

  const {
    data: inspectionsData,
    loading: inspectionsLoading,
    error: inspectionsError,
    fetchInspection,
  } = useFetchInspection();

  const { syncInspection } = useSyncInspection();

  const [params, setParams] = useState<TFetchInspectionProps>();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  const formik = useFormik({
    initialValues: { searchTerm: '', searchField: 'property_code' },
    onSubmit: (values) => {
      setParams({
        page: 1,
        page_size: params?.page_size || DEFAULT_PAGE_SIZE,
        ...(values.searchField === 'property_code'
          ? { property_code: values.searchTerm }
          : { owner_name: values.searchTerm }),
      });
    },
  });

  const handleGetInspections = (today = false) => {
    setParams({
      page: 1,
      page_size: params?.page_size || DEFAULT_PAGE_SIZE,
      ...(today ? { start_date: format(new Date(), 'yyyy-MM-dd') } : {}),
    });
    formik.setFieldValue('searchTerm', '');
  };

  const setPageSize = (n: number) => setParams((p) => ({ ...p, page: 1, page_size: String(n) }));
  const setCurrentPage = (n: number) => setParams((p) => ({ ...p, page: n }));

  const currentPage = Number(params?.page);
  const pageSize = Number(params?.page_size) || Number(DEFAULT_PAGE_SIZE);

  const handleSearch = async () => {
    if (params) {
      await fetchInspection(params);
      if (!inspectionsData && params.property_code) {
        await syncInspection(params.property_code);
        await fetchInspection(params);
      }
    }
  };

  useEffect(() => {
    if (params) handleSearch();
  }, [params]);

  useEffect(() => {
    fetchInspectionQuantity();
  }, [fetchInspectionQuantity]);

  useEffect(() => {
    if (code) {
      setParams({
        page: 1,
        page_size: DEFAULT_PAGE_SIZE,
        property_code: code,
      });
      formik.setFieldValue('searchTerm', code);
      formik.setFieldValue('searchField', 'property_code');
    }
  }, [code]);

  return {
    formik,
    searchTerm: formik.values.searchTerm,
    searchField: formik.values.searchField,
    quantityData,
    quantityLoading,
    quantityError,
    fetchInspectionQuantity,
    inspectionsData,
    inspectionsLoading,
    inspectionsError,
    currentPage,
    pageSize,
    setPageSize,
    setCurrentPage,
    handleGetInspections,
  };
};

export default useInspections;
